import type {FlyErrorPart} from '#lit/utils/errors.js';
import {FlyErrorConcern, FlyErrorSafe} from '#lit/utils/errors.js';

interface HttpError {
  aborted: boolean;
  statusCode?: number;
}

export class AbortedRequestError extends FlyErrorConcern implements HttpError {
  static errorName = 'AbortedRequestError';
  name = 'AbortedRequestError';
  aborted = true;

  constructor(message = 'Request was aborted', parts: FlyErrorPart[] = []) {
    super(message, ...parts);
  }
}

export class DuplicatedRequestError extends FlyErrorSafe implements HttpError {
  static errorName = 'DuplicatedRequestError';
  name = 'DuplicatedRequestError';
  aborted = true;

  constructor(message = 'Request already exists', parts: FlyErrorPart[] = []) {
    super(message, ...parts);
  }
}

export class TimeoutRequestError extends FlyErrorConcern implements HttpError {
  static errorName = 'TimeoutRequestError';
  name = 'TimeoutRequestError';
  aborted = true;

  constructor(message = 'Request took too long', parts: FlyErrorPart[] = []) {
    super(message, ...parts);
  }
}

/**
 * Indicate a malformed request initiated by the user
 */
export class InvalidRequestError extends FlyErrorConcern implements HttpError {
  static errorName = 'InvalidRequestError';
  name = 'InvalidRequestError';
  aborted = false;

  constructor(message = 'Invalid request', parts: FlyErrorPart[] = []) {
    super(message, ...parts);
  }
}

/**
 * Indicate an error occurred on the connection level,
 * probably the request was not handled by the server yet
 */
export class NetworkError extends FlyErrorConcern implements HttpError {
  static errorName = 'NetworkError';
  name = 'NetworkError';
  aborted = false;

  constructor(
    message = 'Error on the network level',
    parts: FlyErrorPart[] = []
  ) {
    super(message, ...parts);
  }
}

export function isDuplicatedRequestError(
  error: unknown
): error is DuplicatedRequestError {
  return (
    error instanceof Error && error.name === DuplicatedRequestError.errorName
  );
}

export function isAbortedRequestError(
  error: unknown
): error is AbortedRequestError {
  return error instanceof Error && error.name === AbortedRequestError.errorName;
}

export function isTimeoutError(error: unknown): error is TimeoutRequestError {
  return error instanceof Error && error.name === TimeoutRequestError.errorName;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof Error && error.name === NetworkError.errorName;
}

export type HttpServiceError =
  | AbortedRequestError
  | DuplicatedRequestError
  | TimeoutRequestError
  | NetworkError
  | SyntaxError;
